import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { saveAuth } from '../auth';
import { auth as authApi, onboarding as onboardingApi } from '../services/api';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [submitLabel, setSubmitLabel] = useState('Entrar');
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [submitStyle, setSubmitStyle] = useState({});
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  function handleAuthSuccess(token, user) {
    saveAuth(token, user);
    if (typeof window.onLoginSuccess === 'function') {
      window.onLoginSuccess(token, user);
    }
  }

  async function getPostLoginRoute() {
    try {
      const data = await onboardingApi.getOnboarding();
      return data?.completed ? '/dashboard' : '/onboarding';
    } catch {
      return '/onboarding';
    }
  }

  React.useEffect(() => {
    if (!showForgot) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowForgot(false);
        setForgotEmail('');
        setForgotMsg('');
        setForgotError('');
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showForgot]);

  async function handleForgotSubmit() {
    if (!forgotEmail) {
      setForgotError('Digite seu e-mail.');
      return;
    }

    setForgotLoading(true);
    setForgotError('');

    try {
      const data = await authApi.forgotPassword(forgotEmail);

      if (data?.ok) {
        setForgotMsg('Link enviado! Verifique sua caixa de entrada e a pasta de spam. O link expira em 1 hora.');
      } else {
        setForgotError(data?.error || 'Erro ao enviar. Tente novamente.');
      }
    } catch (error) {
      setForgotError(error.message || 'Erro ao enviar. Tente novamente.');
    }

    setForgotLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    let valid = true;
    setLoginError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setEmailError(true); valid = false;
    } else setEmailError(false);
    if (!password) {
      setPasswordError(true); valid = false;
    } else setPasswordError(false);
    if (!valid) return;

    setSubmitLabel('Entrando...');
    setSubmitDisabled(true);

    try {
      const data = await authApi.login(email, password);
      handleAuthSuccess(data.token, data.user);
      const nextRoute = await getPostLoginRoute();

      setSubmitLabel('✓ Autenticado! Redirecionando...');
      setSubmitStyle({ background: 'linear-gradient(135deg, #4ADE80, #22c55e)' });
      setTimeout(() => navigate(nextRoute), 1200);
    } catch (err) {
      setLoginError(err.message || 'E-mail ou senha incorretos.');
      setSubmitLabel('Entrar');
      setSubmitDisabled(false);
    }
  }

  return (
    <div className="page">
      {/* ESQUERDA */}
      <div className="left">
        <div className="left-copy">
          <Link to="/" className="left-logo">
            <img src={require('../logo.png')} alt="PokerMind Brasil" style={{ height: '46px', objectFit: 'contain' }} />
          </Link>
        </div>

        <div className="left-copy">
          <p className="left-tag">Bem-vindo de volta</p>
          <h2 className="left-title">Continue sua <span className="grad">evolução</span> no poker</h2>
          <div className="left-stats">
            <div className="stat-card">
              <span className="stat-number">+23%</span>
              <span className="stat-label-card">de winrate médio após 30 dias de uso</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">4.8★</span>
              <span className="stat-label-card">avaliação média dos usuários ativos</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">12k+</span>
              <span className="stat-label-card">mãos analisadas por semana</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">97%</span>
              <span className="stat-label-card">recomendam para outros jogadores</span>
            </div>
          </div>
        </div>

        <div className="left-testimonial">
          <p className="testimonial-text-sm">"O PokerMind virou parte da minha rotina de estudo. Entro toda semana pra revisar as mãos e ajustar meu jogo. É diferente de qualquer outra ferramenta que já usei."</p>
          <div className="testimonial-author-sm">
            <div className="author-avatar-sm">MC</div>
            <div className="author-info-sm">
              <strong>Marcos C.</strong>
              <span>NL100 → NL400 em 6 meses · Curitiba</span>
            </div>
          </div>
        </div>
      </div>

      {/* DIREITA */}
      <div className="right">
        <div className="form-wrap">
          <div className="form-header">
            <h1>Entrar na sua conta</h1>
            <p>Não tem conta? <Link to="/cadastro">Criar grátis</Link></p>
          </div>

          {/* Google OAuth */}
          <button
            type="button"
            className="btn-submit"
            disabled
            title="Em breve"
            style={{ background: '#1f1f1f', color: 'var(--muted)', opacity: 0.7, cursor: 'not-allowed', marginBottom: '0' }}
          >
            Entrar com Google • Em breve
          </button>
          <div className="divider-form"><span>ou com email</span></div>

          {loginError && (
            <div className="alert-error visible">{loginError}</div>
          )}

          <form id="loginForm" noValidate onSubmit={handleSubmit}>
            <div className={`form-group${emailError ? ' has-error' : ''}`}>
              <label htmlFor="login-email">E-mail</label>
              <input
                type="email" id="login-email" className="form-input"
                placeholder="seu@email.com" autoComplete="email"
                value={email} onChange={e => setEmail(e.target.value)}
              />
              <span className="error-msg">E-mail inválido</span>
            </div>
            <div className={`form-group${passwordError ? ' has-error' : ''}`}>
              <div className="label-row">
                <label htmlFor="login-password">Senha</label>
              </div>
              <div className="input-icon-wrap">
                <input
                  type={showPassword ? 'text' : 'password'} id="login-password" className="form-input"
                  placeholder="Sua senha" autoComplete="current-password"
                  value={password} onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button" className="input-icon"
                  onClick={() => setShowPassword(v => !v)}
                  style={{ color: showPassword ? 'var(--yellow)' : 'var(--muted)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
              <span className="error-msg">Digite sua senha</span>
            </div>

            <button
              type="submit" className="btn-submit"
              disabled={submitDisabled}
              style={submitStyle}
            >
              {submitLabel}
            </button>

            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '0.82rem', fontFamily: "'Barlow', sans-serif", textDecoration: 'underline' }}
              >
                Esqueci minha senha
              </button>
            </div>
          </form>

          <div className="signup-cta">
            Novo por aqui? <Link to="/cadastro">Criar conta grátis</Link> — sem cartão de crédito
          </div>
        </div>
      </div>

      {showForgot && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '20px' }}
          onClick={() => { setShowForgot(false); setForgotEmail(''); setForgotMsg(''); setForgotError(''); }}
        >
          <div
            style={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '400px' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ fontSize: '1.1rem', fontFamily: "'Barlow Condensed'", fontWeight: 700, textTransform: 'uppercase', color: 'var(--white)', marginBottom: '12px' }}>Recuperar senha</div>

            {forgotMsg ? (
              <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: '10px', padding: '14px', fontSize: '0.84rem', color: 'var(--green)', lineHeight: 1.6, marginBottom: '20px' }}>
                {forgotMsg}
              </div>
            ) : (
              <>
                <p style={{ fontSize: '0.84rem', color: 'var(--muted)', marginBottom: '16px', lineHeight: 1.6 }}>
                  Digite seu e-mail para receber o link de redefinição de senha.
                </p>
                <input
                  type="email"
                  placeholder="seu@email.com"
                  value={forgotEmail}
                  onChange={(event) => setForgotEmail(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && handleForgotSubmit()}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '11px 14px', color: 'var(--white)', fontFamily: "'Barlow', sans-serif", fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none', marginBottom: '8px' }}
                />
                {forgotError && <div style={{ color: 'var(--red)', fontSize: '0.78rem', marginBottom: '8px' }}>{forgotError}</div>}
              </>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button
                onClick={() => { setShowForgot(false); setForgotEmail(''); setForgotMsg(''); setForgotError(''); }}
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'var(--muted)', padding: '9px 16px', cursor: 'pointer', fontFamily: "'Barlow'", fontSize: '0.82rem' }}
              >
                {forgotMsg ? 'Fechar' : 'Cancelar'}
              </button>
              {!forgotMsg && (
                <button
                  onClick={handleForgotSubmit}
                  disabled={forgotLoading}
                  style={{ background: forgotLoading ? 'rgba(245,200,0,0.4)' : 'var(--yellow)', border: 'none', borderRadius: '8px', color: '#000', padding: '9px 18px', cursor: forgotLoading ? 'not-allowed' : 'pointer', fontFamily: "'Barlow Condensed'", fontWeight: 800, fontSize: '0.86rem', letterSpacing: '.04em', textTransform: 'uppercase' }}
                >
                  {forgotLoading ? 'Enviando...' : 'Enviar link →'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
